/**
 * The primary conversion button.
 *
 * This is the one place in the codebase that emits inline styles, and it is deliberate.
 *
 * On www.collctiv.com the primary CTA is not a Webflow element and has no Webflow class. It
 * is a raw `<button>` with inline styles inside a `w-embed w-script`, wired to a global
 * `handleConversionClick(loc, pos, utmContent)`, and its DOM id doubles as the analytics
 * position — `navBarCTA`, `homeHeroCTA`. PostHog's `conversion_action_selected` event and
 * Mixpanel's `track_links` binding both key off exactly that shape.
 *
 * A tidier button styled with an `lp-` class would look identical and silently stop
 * reporting conversions. So we reproduce their markup verbatim, down to the Tailwind-ish
 * comments they left in the inline style attribute.
 */

import type { CtaValue } from '@lp/shared';

export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
export type ButtonRadius = 'pill' | 'rounded' | 'square';

export const BUTTON_SIZE_OPTIONS = [
  { label: 'Extra small', value: 'xsmall' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
  { label: 'Extra large', value: 'xlarge' },
] as const;

export const BUTTON_RADIUS_OPTIONS = [
  { label: 'Pill', value: 'pill' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Square', value: 'square' },
] as const;

/**
 * Everything that can vary about a button's appearance.
 *
 * Deliberately three closed enumerations rather than a padding/font-size/radius free-for-all.
 * A marketer can make the button noticeably bigger, squarer or full-bleed without being able
 * to produce one that stops looking like a Collctiv button.
 */
export interface ButtonStyle {
  size: ButtonSize;
  radius: ButtonRadius;
  /** Stretch to the container — the usual choice for a CTA on a phone. */
  fullWidth: boolean;
}

export const BUTTON_STYLE_DEFAULTS: ButtonStyle = {
  size: 'medium',
  radius: 'pill',
  fullWidth: false,
};

/** Tolerates a button saved before these options existed. */
export function readButtonStyle(value: Partial<ButtonStyle> | undefined): ButtonStyle {
  return { ...BUTTON_STYLE_DEFAULTS, ...(value ?? {}) };
}

/**
 * Sizes.
 *
 * `medium` is the live site's button, byte for byte — the colours, weight, radius and
 * padding all come straight off `navBarCTA`. The others scale only the type and the padding
 * around it, so a bigger button is still recognisably the same button.
 */
const SIZES: Record<ButtonSize, { fontSize: string; lineHeight: string; padding: string }> = {
  xsmall: { fontSize: '0.8125rem', lineHeight: '1.125rem', padding: '0.375rem 1rem' },
  small: { fontSize: '0.875rem', lineHeight: '1.25rem', padding: '0.5rem 1.25rem' },
  medium: { fontSize: '1rem', lineHeight: '1.5rem', padding: '0.75rem 2rem' },
  large: { fontSize: '1.125rem', lineHeight: '1.75rem', padding: '1rem 2.5rem' },
  xlarge: { fontSize: '1.25rem', lineHeight: '1.875rem', padding: '1.25rem 3rem' },
};

const RADII: Record<ButtonRadius, string> = {
  pill: '9999px',
  rounded: '12px',
  square: '0px',
};

/** Verbatim from the live site's `navBarCTA` apart from the size, radius and width values. */
const buttonStyle = (style: ButtonStyle): string => {
  const s = SIZES[style.size] ?? SIZES.medium;
  return [
    'background-color:#ffde00',
    'color:#1e1b4b',
    'font-weight:500',
    `font-size:${s.fontSize}`,
    `line-height:${s.lineHeight}`,
    `padding:${s.padding}`,
    `border-radius:${RADII[style.radius] ?? RADII.pill}` /* rounded-full */,
    'border:none',
    'cursor:pointer',
    // A full-width button has to be allowed to wrap; a fixed-width one must not.
    style.fullWidth ? 'white-space:normal' : 'white-space:nowrap',
    'outline:none',
    "font-family:'Gt walsheim pro'",
    ...(style.fullWidth ? ['width:100%', 'display:block', 'text-align:center'] : []),
  ].join(';');
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Single-quoted JS string literal, safe for embedding in an inline `<script>`. */
function jsString(s: string): string {
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/<\//g, "<\\/")}'`;
}

/**
 * A DOM id must be unique on the page and is also the analytics position, so a duplicate is
 * both an HTML error and a corrupted funnel. `validate()` on each block surfaces collisions.
 */
export function isValidAnalyticsId(id: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_-]{2,48}$/.test(id);
}

/**
 * Known ids already in use on the live site. Offered as a datalist so authors reuse an
 * existing position rather than inventing a synonym that splits the funnel.
 */
export const KNOWN_ANALYTICS_IDS = [
  'navBarCTA',
  'homeHeroCTA',
  'homeMidCTA',
  'homeFooterCTA',
  'lpHeroCTA',
  'lpMidCTA',
  'lpFooterCTA',
] as const;

/**
 * Build the embed HTML.
 *
 * `handleConversionClick` is defined once in the site's footer custom code, so we call it
 * rather than redefining it — redefining would break the moment they change the signup URL.
 */
export function ctaEmbedHtml(cta: CtaValue, style?: Partial<ButtonStyle>): string {
  const id = cta.analyticsId || 'lpCTA';
  const label = escapeHtml(cta.label || 'Collect money now');
  const args = [cta.location || 'website', id, cta.utmContent || id].map(jsString).join(',');

  return (
    `<button id="${escapeHtml(id)}" style="${buttonStyle(readButtonStyle(style))}">${label}</button>` +
    `<script>document.getElementById(${jsString(id)})` +
    `.addEventListener('click', () => { handleConversionClick(${args}) });</script>`
  );
}

/**
 * App-store badges.
 *
 * The class pairing is load-bearing and has no visual effect: a footer script calls
 * ipwhois and toggles `.usdownload` visible / `.ukdownload` hidden for US visitors. Omit
 * either class and the badges either never appear or never switch — with no error anywhere.
 * That is why `AppStoreBadges` exposes no styling options at all.
 */
export function storeBadgeClass(geo: 'uk' | 'us'): string {
  return geo === 'us' ? 'usdownload' : 'ukdownload';
}

/** Mixpanel's `track_links(".downloadmp", …)` binds to this class. */
export const MIXPANEL_DOWNLOAD_CLASS = 'downloadmp';

/** Mixpanel's conversion-link binding, used on `/lp/` pages. */
export const MIXPANEL_CONVERSION_CLASS = 'cmn';
